import React, { useEffect, useState } from "react";
import GeneralListAction from "./GeneralListAction";
import ToolsApi from "../../../api/ToolsApi";
import CalibrationApi from "../../../api/CalibrationApi";
import ImagerApi from "../../../api/ImagerApi";
import TemperatureApi from "../../../api/TemperatureApi";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import { getModulesByType } from "../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../Model/EModuleType";
import { ETemperatureControlFeature } from "../../../Model/Temperature";
import { getModuleCapability } from "../../../redux/reducers/ModuleCapabilitiesReducer";
import { EBarcodeScannerType } from "../../../Model/PsrCapabilites";
import "./ToolsGeneral.css";
import { getModuleState } from "../../../redux/reducers/ModuleStateReducer";

type Props = {
  style?: React.CSSProperties;
};

export default function GeneralToolsActionGroup({ style }: Props) {
  const [temperatureControlFeatures, setTemperatureControlFeatures] = useState(
    null as ETemperatureControlFeature[] | null
  );

  const chamberModules = useTypedSelector(
    (state) => getModulesByType(state)[EModuleType.CHAMBER]
  );
  const imagerModules = useTypedSelector(
    (state) => getModulesByType(state)[EModuleType.IMAGER]
  );
  const psrModules = useTypedSelector(
    (state) => getModulesByType(state)[EModuleType.PSR]
  );

  const psrModule = psrModules[0];

  const onClickHome = () => {
    ToolsApi.home();
  };

  const onClickFindCarrierMaxPosition = () => {
    CalibrationApi.findCarrierMaxPosition(psrModule.identifier);
  };

  const onClickRetract = (imagerIdentifier: string) => {
    ImagerApi.retract(imagerIdentifier);
  };

  const onClickEject = (imagerIdentifier: string) => {
    ImagerApi.eject(imagerIdentifier);
  };

  const onClickClearFanError = () => {
    TemperatureApi.clearFanError(chamberModules[0].identifier);
  };

  const onClickClearHeaterError = () => {
    TemperatureApi.clearHeaterError(chamberModules[0].identifier);
  };

  useEffect(() => {
    TemperatureApi.getTemperatureControlFeatures(
      chamberModules[0].identifier
    ).then((value) => setTemperatureControlFeatures(value));
  }, [chamberModules[0].identifier]);

  const psrIdentifier = psrModule.identifier;
  const psrCapability = useTypedSelector((state) =>
    getModuleCapability(state, psrIdentifier)
  ).psr;

  const isSupportBarcode =
    psrCapability !== undefined
      ? psrCapability.barcodeScanner.barcodeScannerType ===
        EBarcodeScannerType.HoneywellN431XScanner
      : false;

  const isBarcodeScanning = useTypedSelector(
    (state) =>
      getModuleState(state, psrModule.identifier)?.psr?.barcodeScanner
        ?.isScanning ?? false
  );

  const [isBarcodeTriggeredOn, setIsBarcodeTriggeredOn] =
    useState(isBarcodeScanning);

  useEffect(() => {
    setIsBarcodeTriggeredOn(isBarcodeScanning);
  }, [isBarcodeScanning]);

  const onTriggerBarcodeScan = () => {
    if (isBarcodeTriggeredOn) {
      ToolsApi.barcodeScannerStop();
    } else {
      ToolsApi.barcodeScannerStart();
    }
    setIsBarcodeTriggeredOn(!isBarcodeTriggeredOn);
  };

  if (temperatureControlFeatures == null) {
    return <div>Loading temperature control features...</div>;
  }

  const hasHeater = temperatureControlFeatures.includes(
    ETemperatureControlFeature.Heater
  );
  const hasFan = temperatureControlFeatures.includes(
    ETemperatureControlFeature.Fan
  );

  return (
    <div style={style} className="tools-general">
      <div className="tools-general-list-title">Psr Movement</div>
      <GeneralListAction
        title="Homing Robots"
        onActionTrigger={onClickHome}
        type="button"
        buttonText="START"
      />
      <GeneralListAction
        title="Find Carrier Max Position"
        onActionTrigger={onClickFindCarrierMaxPosition}
        type="button"
        buttonText="START"
      />
      {imagerModules.length > 0 && (
        <div className="tools-general-list-title">Imager Movement</div>
      )}
      {imagerModules.map((imagerModule) => (
        <div key={imagerModule.identifier}>
          <GeneralListAction
            title="Retract Vis-UV Imager"
            onActionTrigger={() => {
              onClickRetract(imagerModule.identifier);
            }}
            type="button"
            buttonText="START"
          />
          <GeneralListAction
            title="Eject Vis-UV Imager"
            onActionTrigger={() => {
              onClickEject(imagerModule.identifier);
            }}
            type="button"
            buttonText="START"
          />
        </div>
      ))}
      {(hasFan || hasHeater) && (
        <div className="tools-general-list-title">Clean Error</div>
      )}
      {hasFan && (
        <GeneralListAction
          title="Clear Fan Error"
          onActionTrigger={onClickClearFanError}
          type="button"
          buttonText="CLEAR"
        />
      )}
      {hasHeater && (
        <GeneralListAction
          title="Clear Heater Error"
          onActionTrigger={onClickClearHeaterError}
          type="button"
          buttonText="CLEAR"
        />
      )}

      {isSupportBarcode && (
        <>
          <div className="tools-general-list-title">Switch On / Off</div>
          <GeneralListAction
            title="Barcode Scanner"
            onActionTrigger={onTriggerBarcodeScan}
            type="switch"
            isOnTrigger={isBarcodeTriggeredOn}
          />
        </>
      )}
    </div>
  );
}
